'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import {
  Accessibility,
  ArrowRight,
  Bell,
  BookOpen,
  Building2,
  BookMarked,
  LayoutGrid,
  CalendarDays,
  CheckCircle2,
  Clock,
  Filter,
  GraduationCap,
  HeartHandshake,
  LocateFixed,
  Map,
  MapPin,
  MessageCircle,
  Mic,
  Navigation,
  ParkingCircle,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  Wrench,
  X,
  ChevronDown,
} from 'lucide-react'
import { TIER_BADGE_COLORS, NO_TIER_BADGE } from '../../lib/demo-data'
import { masjidPlaceholder } from '../../lib/masjid-placeholder'
import { sourceBadgeLabel } from '../../lib/provenance'
import { nextPrayerFrom } from '../../lib/prayer-next'
import dyn from 'next/dynamic'
import { MasjidKitCard, AIBadge, PageHero } from '../../components/ui/kit'
import { formatAddedDate } from '../../lib/format-number'

/* PERF: mount-on-approach — the Leaflet bundle is fetched only when the map
   section nears the viewport, keeping it out of mobile TBT entirely */
function useInView(margin = '600px') {
  const ref = useRef<HTMLDivElement | null>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect() }
    }, { rootMargin: margin })
    io.observe(el)
    return () => io.disconnect()
  }, [inView, margin])
  return { ref, inView }
}

const DirectoryLiveMap = dyn(() => import('../../components/directory/DirectoryLiveMap'), { ssr: false, loading: () => <div style={{ height: 380, borderRadius: 18, background: 'linear-gradient(160deg,#101A38,#0B132B)', display: 'grid', placeItems: 'center', color: '#8A94A8', fontFamily: 'var(--f-mono)', fontSize: 11, letterSpacing: '.14em' }}>LOADING LIVE MAP…</div> })
import DirectoryGlance from '../../components/directory/DirectoryGlance'
import { NextJumuahWidget } from './NextJumuahWidget'
import { useFeatureFlags } from '../../hooks/useFeatureFlags'
import {
  AIAlert,
  AIRecommendation,
  ActivityTimeline,
  Button,
  ButtonLink,
  DashboardSection,
  GridSystem,
  MetricPill,
  MobileStack,
  PremiumCard,
  PremiumMetricCard,
  SectionContainer,
  StatusBadge,
} from '../../components/ui'
import type { GraphRecommendation, GraphSignals } from '../../lib/graph-client'
import { civicEventToGraphActivity, civicEventToRecommendation, fetchGraphJson, recommendationHref } from '../../lib/graph-client'
import { useCivicRealtime } from '../../hooks/useCivicRealtime'
import AdSlot from '../../components/ui/AdSlot'
import UmmahRail from '../../components/ummah/UmmahRail'
import { FACILITY_ATTR as FILTER_ATTR } from '../../lib/facility-attrs'
import { searchTerms } from '../../lib/search-terms'

type Masjid = {
  id: string
  slug: string
  nameEnglish: string
  nameUrdu?: string | null
  city?: string | null
  district?: string | null
  state?: string | null
  addressLine1?: string | null
  sect?: string | null
  capacity?: number | null
  establishedYear?: number | null
  verified?: boolean | null
  description?: string | null
  latitude?: string | number | null
  longitude?: string | number | null
  coverPhoto?: string | null
  photoReady?: boolean | null
  photos?: string[] | null
  facilities?: string[] | null
  isWaqfRegistered?: boolean | null
  subscriptionStatus?: string | null
  createdAt?: string | null
  importSource?: string | null
}

const PER_PAGE = 9

type GraphSignalLike = {
  id?: string
  type?: string
  metadata?: Record<string, unknown>
  summary?: string
}

function isRecommendation(item: GraphRecommendation | null | undefined): item is GraphRecommendation {
  return Boolean(item && typeof item.title === 'string' && item.title.length > 0)
}

function graphSignalTitle(item: GraphSignalLike) {
  const type = item.type || 'notification_created'
  return String(item.metadata?.title || item.summary || type.replace(/_/g, ' '))
}
const SECTS = ['All', 'Sunni', 'Deobandi', 'Barelvi', 'Ahle Hadith', 'Shia', 'Salafi']
/* Sect facet hidden until the data exists: the real masjid records (OSM import)
   almost never carry a `sect` value, so the facet UI would return empty/wrong results
   (honest > broken). Flip to true once sect data is populated. The server-side sect filter
   now EXISTS in /api/masjids (smart-search relies on it), so enabling the facet is UI-only. */
const SECT_FACET_ENABLED = false
const CITIES = ['All Cities', 'Hyderabad', 'Mumbai', 'Delhi', 'Chennai', 'Bangalore', 'Kolkata', 'Pune', 'Lucknow']
/* client-safe group list (collections.ts imports db, so it can't be imported here) */
const COLLECTION_GROUPS = [
  { slug: 'heritage', title: 'Heritage & History' },
  { slug: 'tradition', title: 'Tradition' },
  { slug: 'facilities', title: 'Facilities & Services' },
  { slug: 'jummah-language', title: 'Jummah & Language' },
  { slug: 'location', title: 'Location' },
  { slug: 'seasonal', title: 'Seasonal & Occasions' },
]

const FILTERS = [
  { id: 'Women Section', label: 'Women access', icon: Users },
  { id: 'Wheelchair Accessible', label: 'Wheelchair', icon: Accessibility },
  { id: 'Quran Classes', label: 'Quran classes', icon: BookOpen },
  { id: 'Parking', label: 'Parking', icon: ParkingCircle },
  { id: 'Live Events', label: 'Live events', icon: CalendarDays },
  { id: 'Wudu Area', label: 'Wudu area', icon: CheckCircle2 },
]


const mapNodes = [
  { x: 28, y: 34, size: 14, label: 'Education cluster' },
  { x: 42, y: 24, size: 10, label: 'Verified masjid' },
  { x: 58, y: 36, size: 18, label: 'Friday capacity' },
  { x: 73, y: 48, size: 12, label: 'Live event' },
  { x: 39, y: 62, size: 11, label: 'Volunteer need' },
  { x: 58, y: 74, size: 15, label: 'Waqf record' },
]

/* FILTER_ATTR (label → attributes jsonb key) is now shared with the server via
   src/lib/facility-attrs.ts, so the client filter and /api/masjids match a facility the
   same way (facilities[] text OR attributes[key]===true). Imported at the top of this file. */

function includesFacility(masjid: Masjid, facility: string) {
  const attrKey = FILTER_ATTR[facility]
  if (attrKey && (masjid as any).attributes?.[attrKey] === true) return true
  return (masjid.facilities || []).some((value) => value.toLowerCase().includes(facility.toLowerCase()))
}

function getFacilities(masjid: Masjid) {
  const values = masjid.facilities?.filter(Boolean) || []
  if (values.length) return values.slice(0, 4)
  return ['Prayer hall', 'Wudu area', 'Community access']
}

function getTransparencyScore(masjid: Masjid) {
  let score = 62
  if (masjid.verified) score += 14
  if (masjid.isWaqfRegistered) score += 10
  if (masjid.facilities?.length) score += Math.min(10, masjid.facilities.length * 2)
  if (masjid.capacity) score += 4
  return Math.min(98, score)
}

/* Real calculated prayer clock for the visitor's city (Aladhan via /api/prayer).
   One fetch for the page — every card shows the same true next prayer. */
function useNextPrayer(city: string) {
  const [next, setNext] = useState<{ name: string; time: string } | null>(null)
  useEffect(() => {
    let alive = true
    const target = city === 'All Cities' ? 'Hyderabad' : city
    fetch(`/api/prayer?city=${encodeURIComponent(target)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d) return
        const np = nextPrayerFrom(d)
        if (np) setNext({ name: np.name.charAt(0) + np.name.slice(1).toLowerCase(), time: np.time })
      })
      .catch(() => {})
    return () => { alive = false }
  }, [city])
  return next
}

function DirectoryMap({ count }: { count: number }) {
  return (
    <div className="directory-map-surface">
      <div className="directory-map-noor" />
      <div className="directory-map-grid" />
      <div className="directory-map-density directory-density-a" />
      <div className="directory-map-density directory-density-b" />
      <div className="directory-india-silhouette" />
      <div className="directory-map-hud">
        <span>Live grid</span>
        <strong>Masjid density view</strong>
        <div>
          <b>{count.toLocaleString('en-IN')}</b> masjids indexed
        </div>
      </div>
      <svg viewBox="0 0 620 430" aria-hidden="true" className="directory-map-lines">
        <path d="M88 300 C180 220 220 120 320 150 C430 183 460 88 545 118" />
        <path d="M130 120 C210 190 318 170 402 246 C470 306 510 262 570 324" />
        <path d="M188 356 C260 270 322 272 410 198 C460 155 500 176 558 212" />
      </svg>
      {mapNodes.map((node, index) => (
        <div
          key={node.label}
          className="directory-map-node"
          style={{ left: `${node.x}%`, top: `${node.y}%`, width: node.size, height: node.size, animationDelay: `${index * 0.24}s` }}
        >
          <span>{node.label}</span>
        </div>
      ))}
      <div className="directory-map-card">
        <StatusBadge tone="emerald">Live</StatusBadge>
        <strong>{count.toLocaleString('en-IN')} institutions indexed</strong>
        <p>Verified masjid density, active events, education programs, and community services.</p>
      </div>
      <div className="directory-map-legend">
        <span><i /> Verified masjids</span>
        <span><i /> Volunteer movement</span>
        <span><i /> Waqf awareness</span>
      </div>
    </div>
  )
}

function FilterChipButton({ active, label, onClick, icon: Icon }: { active?: boolean; label: string; onClick: () => void; icon?: typeof Filter }) {
  return (
    <button className={`directory-filter-chip ${active ? 'is-active' : ''}`} onClick={onClick} type="button">
      {Icon && <Icon size={15} />}
      {label}
    </button>
  )
}

function MasjidDiscoveryCard({ masjid, index, showTier, nextPrayer }: { masjid: Masjid; index: number; showTier: boolean; nextPrayer: { name: string; time: string } | null }) {
  const score = getTransparencyScore(masjid)
  const plan = masjid.subscriptionStatus || ''  // '' = unclaimed / no tier
  const planBadge = TIER_BADGE_COLORS[plan] || NO_TIER_BADGE
  const facilities = getFacilities(masjid)
  const featured = Boolean(masjid.verified)

  return (
    <Link href={`/masjids/${masjid.slug}`} className={`directory-masjid-card ${featured ? 'is-featured' : ''}`}>
      <div
        className={`directory-card-visual directory-card-visual-${index % 4}`}
        style={{
          backgroundImage: `url(${masjid.coverPhoto || masjidPlaceholder(masjid.slug)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="directory-card-arch" />
        <div className="directory-card-visual-glow" />
        <span>{masjid.verified ? 'Verified profile' : sourceBadgeLabel(masjid.importSource).label}</span>
        {nextPrayer && <b>{nextPrayer.name} {nextPrayer.time}</b>}
      </div>
      <div className="directory-card-topline">
        <div>
          <h3>{masjid.nameEnglish}</h3>
          <p><MapPin size={14} /> {[masjid.district, masjid.city].filter(Boolean).join(', ') || 'Location pending'}</p>
        </div>
        {masjid.verified ? <StatusBadge tone="emerald">Verified</StatusBadge> : <StatusBadge tone="warning">Review</StatusBadge>}
      </div>

      <div className="directory-card-metrics">
        <div>
          <span>Next prayer</span>
          <strong>{nextPrayer ? nextPrayer.name : '—'}</strong>
          <small>{nextPrayer ? nextPrayer.time : 'Calculated'}</small>
        </div>
        <div title="Masjid Health Score - computed from verification status, waqf registration, listed facilities, and capacity data.">
          <span>Health score</span>
          <strong>{score}</strong>
          <small>From listed data</small>
        </div>
        <div>
          <span>Status</span>
          <strong>{masjid.verified ? 'Verified' : 'Listed'}</strong>
          <small>{masjid.isWaqfRegistered ? 'Waqf registered' : masjid.verified ? 'Committee' : 'Claimable'}</small>
        </div>
      </div>

      <div className="directory-facility-row">
        {facilities.map((facility) => <span key={facility}>{facility}</span>)}
      </div>

      <div className="directory-ai-note">
        <Sparkles size={15} />
        <span>{masjid.isWaqfRegistered ? 'AI found waqf registration signals and stable committee activity.' : 'AI recommends verifying committee details before routing donations.'}</span>
      </div>

      <div className="directory-card-footer">
        <span>{masjid.sect || 'Masjid'} {masjid.capacity ? `- ${masjid.capacity.toLocaleString('en-IN')} capacity` : ''}{masjid.createdAt ? ` · Added ${formatAddedDate(masjid.createdAt)}` : ''}</span>
        {showTier && planBadge && <b style={{ color: planBadge.color, background: planBadge.bg }}>{planBadge.label}</b>}
      </div>
    </Link>
  )
}

/* PERF P2: the first page of directory records is server-fed — masjid cards
   are in the initial HTML; client fetches run only on filter changes. */
export default function DirectoryClient({ initialMasjids, initialTotal, classificationOn = false }: { initialMasjids: Masjid[]; initialTotal: number; classificationOn?: boolean }) {
  const { isFlagEnabled } = useFeatureFlags()
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [city, setCity] = useState('All Cities')
  const [sect, setSect] = useState('All')
  const [locality, setLocality] = useState('')  // AI-parsed neighbourhood/area → API locality param (Defect C)
  const [tier, setTier] = useState<'All' | 'Verified' | 'Listed'>('All')
  // Lifecycle view: functioning masajid (default) / dormant / shaheed (remembrance). Opt-in;
  // the default 'masjid' view never labels cards. Wired to the /api/masjids classification param.
  const [classification, setClassification] = useState<'masjid' | 'dormant' | 'shaheed'>('masjid')
  const [facilityFilters, setFacilityFilters] = useState<string[]>([])
  // Prayer-time filter/sort — DARK behind `prayerTimeFilter`. COMMITTEE-CONFIRMED times only (the
  // server enforces confirmed-only + never ranks estimates). Hidden until the flag is flipped.
  const prayerFilterOn = isFlagEnabled('prayerTimeFilter')
  const [confirmedPrayerOnly, setConfirmedPrayerOnly] = useState(false)
  const [prayerSort, setPrayerSort] = useState<'' | 'fajr' | 'dhuhr' | 'asr' | 'maghrib' | 'isha'>('')
  const [prayerSortDir, setPrayerSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [showFilterDrawer, setShowFilterDrawer] = useState(false)
  const [mapExpanded, setMapExpanded] = useState(false)
  const mapGate = useInView()
  const [masjids, setMasjids] = useState<Masjid[]>(initialMasjids)
  const [total, setTotal] = useState(initialTotal)
  // Honest legibility: totalAll = same filters counting rows without a photo too (D).
  const [totalAll, setTotalAll] = useState(initialTotal)
  // "Use my location → nearby only" (C). When set, the fetch adds lat/lng + a 25km radius.
  const [nearby, setNearby] = useState<{ lat: number; lng: number } | null>(null)
  const NEARBY_RADIUS_KM = 25
  const [loading, setLoading] = useState(initialMasjids.length === 0)
  const [error, setError] = useState('')
  const nextPrayer = useNextPrayer(city)
  const [hoveredSlug, setHoveredSlug] = useState<string | null>(null)
  const [facilityMenuOpen, setFacilityMenuOpen] = useState(false)
  /* Smart NL search (Phase 3): AI-parsed filters + the chips it derived */
  const [smartChips, setSmartChips] = useState<string[] | null>(null)
  const [smartKeywords, setSmartKeywords] = useState('')
  const [aiSearching, setAiSearching] = useState(false)
  /* rail widgets (D3): daily-fresh records + per-city counts from the pins API */
  const [fresh, setFresh] = useState<any>(null)
  const [topCities, setTopCities] = useState<Array<{ city: string; n: number }>>([])
  const fetchSeqRef = useRef(0)

  useEffect(() => {
    let alive = true
    fetch('/api/fresh').then((r) => (r.ok ? r.json() : null)).then((d) => { if (alive) setFresh(d) }).catch(() => {})
    fetch('/api/masjids/map').then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (!alive || !d?.pins) return
      // (lucide's Map icon import shadows the global Map here — use a record)
      const counts: Record<string, number> = {}
      for (const p of d.pins) if (p.city) counts[p.city] = (counts[p.city] || 0) + 1
      setTopCities(Object.entries(counts).map(([c, n]) => ({ city: c, n })).sort((a, b) => b.n - a.n).slice(0, 6))
    }).catch(() => {})
    return () => { alive = false }
  }, [])
  const [platformMasjids, setPlatformMasjids] = useState(0)
  useEffect(() => {
    fetch('/api/stats').then((r) => (r.ok ? r.json() : null)).then((d) => { if (d?.masjids) setPlatformMasjids(d.masjids) }).catch(() => {})
  }, [])

  // honor /directory?city=… and ?q=… links (homepage map "Explore {city}")
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search)
      const cityParam = sp.get('city')
      const qParam = sp.get('q')
      if (cityParam) setCity(cityParam)
      if (qParam) { setSearch(qParam); setQuery(qParam) }
    } catch {}
  }, [])

  const fetchMasjids = useCallback(async () => {
    setLoading(true)
    setError('')
    const seq = ++fetchSeqRef.current

    try {
      const params = new URLSearchParams()
      if (query) params.set('q', query)
      if (city !== 'All Cities') params.set('city', city)
      if (sect !== 'All') params.set('sect', sect)
      if (locality) params.set('locality', locality)   // Defect C: AI locality → dedicated param (not folded into q)
      // Lifecycle view — always scoped to the selected classification. Default 'masjid' =
      // functioning masajid only (dormant/shaheed are opt-in via the view selector). The API
      // validates the value against ['masjid','shaheed','dormant'].
      params.set('classification', classification)
      // Server-side facilities filter (B) — composes with everything else.
      if (facilityFilters.length) params.set('facilities', facilityFilters.join(','))
      // Prayer-time filter/sort — sent ONLY when the flag is on (else the section is hidden). Confirmed
      // committee times only; the server never ranks estimates.
      if (prayerFilterOn && confirmedPrayerOnly) params.set('confirmedPrayer', '1')
      if (prayerFilterOn && prayerSort) { params.set('prayerSort', prayerSort); params.set('prayerSortDir', prayerSortDir) }
      // Nearby-only (C): the API restricts to masajid within NEARBY_RADIUS_KM and sorts by distance.
      if (nearby) {
        params.set('lat', String(nearby.lat))
        params.set('lng', String(nearby.lng))
        params.set('radiusKm', String(NEARBY_RADIUS_KM))
      }
      // Load the FULL approved set so the in-memory pagination can walk every masjid — raised
      // with the photos-only browse removal (2,243 approved today; the API clamps at 3000).
      params.set('limit', '3000')

      const res = await fetch(`/api/masjids?${params.toString()}`)
      if (!res.ok) throw new Error('Failed to fetch masjids')
      const data = await res.json()
      if (seq !== fetchSeqRef.current) return // a newer fetch superseded this one
      const records = data.masjids || []
      /* HONESTY: zero matches render the honest empty state — never demo rows presented as
         results. (`??` not `||` so a genuine total of 0 stays 0.) */
      setMasjids(records)
      setTotal(data.total ?? records.length)
      setTotalAll(data.totalAll ?? data.total ?? records.length)
    } catch (e) {
      if (seq !== fetchSeqRef.current) return
      /* HONESTY: a failed fetch shows the error banner over an EMPTY grid — never preview/demo
         rows dressed up as live results. */
      setMasjids([])
      setTotal(0)
      setTotalAll(0)
      setError('Live directory data is temporarily unavailable. Please try again shortly.')
      console.error('Directory fetch error:', e)
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false)
    }
  }, [city, query, sect, locality, classification, classificationOn, facilityFilters, nearby, prayerFilterOn, confirmedPrayerOnly, prayerSort, prayerSortDir])

  useEffect(() => {
    // Always load the full approved set on mount (the server-fed initialMasjids is
    // only a fast first-paint preview) AND on every city/query/sect change — so a
    // plain /directory visit can page through ALL approved masjids, not just 18.
    fetchMasjids()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMasjids])


  const toggleFacility = useCallback((facility: string) => {
    setFacilityFilters((current) =>
      current.includes(facility) ? current.filter((item) => item !== facility) : [...current, facility]
    )
    setPage(1)
  }, [])

  const filtered = useMemo(() => {
    // when AI parsed the query, filter by its extracted keywords, not the raw phrase
    /* TOKENIZED re-filter (same shared search-terms lib as the server) — the old full-phrase
       substring match on a slug-less haystack silently DROPPED legitimate server matches:
       "masjid in Hyderabad" matched nothing, and slug-only server hits vanished client-side.
       All-stopword queries yield no terms → no text constraint (mirrors the server). */
    const terms = searchTerms(smartChips ? smartKeywords : search)
    return masjids.filter((masjid) => {
      if (terms.length) {
        const haystack = [masjid.nameEnglish, masjid.slug, masjid.city, masjid.district, masjid.state, masjid.sect].filter(Boolean).join(' ').toLowerCase()
        if (!terms.every((t) => haystack.includes(t))) return false
      }
      if (facilityFilters.length && !facilityFilters.every((facility) => includesFacility(masjid, facility))) return false
      if (tier === 'Verified' && !masjid.verified) return false
      if (tier === 'Listed' && masjid.verified) return false
      return true
    }).sort((a, b) => {
      if (Boolean(a.verified) !== Boolean(b.verified)) return a.verified ? -1 : 1
      return a.nameEnglish.localeCompare(b.nameEnglish)
    })
  }, [facilityFilters, masjids, search, smartChips, smartKeywords, tier])

  const mapRecords = useMemo(() => filtered
    .map((m) => ({ slug: m.slug, name: m.nameEnglish, city: m.city, verified: m.verified, importSource: m.importSource, lat: Number(m.latitude), lng: Number(m.longitude) }))
    .filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng) && m.lat !== 0), [filtered])

  const verifiedCount = filtered.filter((masjid) => masjid.verified).length
  const educationCount = filtered.filter((masjid) => includesFacility(masjid, 'Quran')).length
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE))
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  // Featured = REAL verified masjids that also carry a cover photo (a genuine
  // quality signal an admin/committee set). Top 5; section hides when there are
  // none — never random, never fabricated.
  const featuredMasjids = useMemo(
    () => masjids.filter((m) => m.verified && (m.coverPhoto || (m as any).photo)).slice(0, 5),
    [masjids],
  )

  /* Smart NL search (Phase 3): multi-word queries go through the AI parser
     ("masjid with women's section near Tolichowki" → city/sect/facility
     filters + keywords). Single words and parser failures fall back to the
     plain text search unchanged. */
  const handleSearch = async () => {
    const text = search.trim()
    setPage(1)
    if (text.split(/\s+/).length >= 3) {
      setAiSearching(true)
      /* The AI parse is an ENHANCEMENT, never a dependency: 5s cap (there is no server-side
         timeout, and the SDK default is ~10min — a hanging provider used to pin the button on
         "Reading…"). On timeout or {parsed:null} we fall through to plain search SILENTLY —
         no error shown, no pretence the AI ran. */
      const abort = new AbortController()
      const timer = setTimeout(() => abort.abort(), 5000)
      try {
        const res = await fetch('/api/ai/parse-search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: text }),
          signal: abort.signal,
        })
        clearTimeout(timer)
        const d = res.ok ? await res.json() : null
        const p = d?.parsed
        if (p) {
          if (p.city) setCity(p.city)
          if (p.sect) setSect(p.sect)
          if (p.facilities?.length) setFacilityFilters(p.facilities)
          // Defect C: locality goes to the dedicated locality param (server-side), NOT folded
          // into the free-text q — so a locality-only query filters properly.
          setLocality(p.locality || '')
          const kw = p.keywords || ''
          setSmartKeywords(kw)
          setQuery(kw)
          setSmartChips([p.city, p.locality, p.sect, ...(p.facilities || [])].filter(Boolean))
          setAiSearching(false)
          return
        }
      } catch { clearTimeout(timer) /* parser unavailable/timed out — plain search below */ }
      setAiSearching(false)
    }
    setSmartChips(null)
    setSmartKeywords('')
    setLocality('')
    setQuery(text)
  }

  const clearFilters = () => {
    setSearch('')
    setQuery('')
    setLocality('')
    setCity('All Cities')
    setSect('All')
    setTier('All')
    setFacilityFilters([])
    setSmartChips(null)
    setSmartKeywords('')
    setPage(1)
  }

  return (
    <div className="directory-premium-page">

      {/* Canonical hero (DESIGN_SYSTEM §2) — one shared PageHero on every page.
          The AI smart-search panel rides in the consistent side-card slot. */}
      <PageHero
        motif="directory"
        eyebrow="Masjid discovery network"
        title="Find a masjid near you"
        subtitle="Namaz timings, jumma times, facilities and announcements for every listed masjid across India."
        actions={
          <div className="directory-hero-signal-strip" aria-label="Directory intelligence signals">
            <span><ShieldCheck size={14} /> {verifiedCount} verified in view</span>
            <span><Users size={14} /> Women&apos;s access mapped</span>
            {nextPrayer && <span><Bell size={14} /> {nextPrayer.name} {nextPrayer.time}</span>}
          </div>
        }
        widget={
          <PremiumCard className="directory-search-panel">
            <div className="directory-search-kicker">
              <span>Smart search</span>
              <StatusBadge tone="emerald">Live</StatusBadge>
            </div>
            <div className="directory-ai-search">
              <Sparkles size={19} />
              <input
                value={search}
                onChange={(event) => { setSearch(event.target.value); setSmartChips(null); setPage(1) }}
                onKeyDown={(event) => event.key === 'Enter' && handleSearch()}
                placeholder="Ask AI: masjids near me with Quran classes and parking"
              />
              <Button onClick={handleSearch} rightIcon={<ArrowRight size={15} />} disabled={aiSearching}>{aiSearching ? 'Reading…' : 'Search'}</Button>
            </div>

            {smartChips && smartChips.length > 0 && (
              <div className="directory-smart-read" aria-label="How AI read your search">
                <AIBadge label="AI READ" />
                {smartChips.map((chip) => <span key={chip} className="directory-smart-chip">{chip}</span>)}
                <button type="button" onClick={clearFilters}>clear</button>
              </div>
            )}

            <div className="directory-trending">
              {['Friday prayer nearby', 'Women access', 'Quran classes', 'Waqf registered', 'Volunteer needs'].map((item) => (
                <button key={item} type="button" onClick={() => { setSearch(item); setQuery(item); setPage(1) }}>{item}</button>
              ))}
            </div>
          </PremiumCard>
        }
      />

      <section className="directory-sticky-search">
        <div className="container-page">
          {/* A real <form> so the mobile keyboard's Enter/Go key submits — this bar is
              mobile-only and previously had NO Enter handler at all. */}
          <form className="directory-sticky-shell" onSubmit={(event) => { event.preventDefault(); handleSearch() }}>
            <Search size={16} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search masjids, facilities, areas" enterKeyHint="search" />
            <Button size="sm" type="submit">Search</Button>
          </form>
        </div>
      </section>

      {/* Phase 3 (D) — calm, SECONDARY links to the other classification surfaces. Shown ONLY
          when the masjidClassification flag is ON; the active directory above stays priority #1
          and uncluttered. Flag OFF ⇒ this whole block is absent (zero visible change). */}
      {classificationOn && (
        <section className="container-page" aria-label="Other masjid records">
          {/* Pillar destinations — token-driven chips (Shaheed navy/gold, Adopt emerald/gold),
              not one-off inline greys. These carry two of the platform's pillars. */}
          <div className="directory-pillar-row">
            <span className="directory-pillar-label">Also on Masjids.in:</span>
            <a href="/masjids/shaheed" className="directory-pillar-chip is-shaheed">
              <BookMarked size={14} /> Shaheed archive <ArrowRight size={13} />
            </a>
            <a href="/masjids/adopt" className="directory-pillar-chip is-adopt">
              <Wrench size={14} /> Adopt a Masjid <ArrowRight size={13} />
            </a>
          </div>
        </section>
      )}

      <SectionContainer>
        <div className="directory-main-layout">
          <div className="directory-primary">
            {/* Next Jumu'ah near you — up to 3 nearby upcoming jamaats (real committee times only).
                Self-time-boxed (Thu → pre-Friday-Asr) + self-empty-CTA; renders nothing outside its
                window. Mirrors the mobile Discover widget. */}
            <NextJumuahWidget city={city === 'All Cities' ? undefined : city} />
            {/* directory_top REMOVED: a leaderboard between the search bar and its results
                interrupts the scan path — the worst kind of interruption. The directory keeps
                two placements (in-grid every 9 cards + the rail), so inventory is preserved
                without an ad splitting query from answer. */}

            {featuredMasjids.length > 0 && (
              <DashboardSection title="Featured masjids" eyebrow="Verified &amp; community-confirmed">
                <div className="directory-kit-grid">
                  {featuredMasjids.map((masjid) => (
                    <div key={`feat-${masjid.id || masjid.slug}`} className="directory-kit-cell">
                      <MasjidKitCard
                        m={{ id: masjid.id, slug: masjid.slug, name: masjid.nameEnglish, area: masjid.district, city: masjid.city, verified: masjid.verified, coverPhoto: masjid.coverPhoto || (masjid as any).photo, photoReady: masjid.photoReady, capacity: masjid.capacity, facilities: masjid.facilities , importSource: masjid.importSource }}
                        nextPrayer={nextPrayer}
                      />
                    </div>
                  ))}
                </div>
              </DashboardSection>
            )}

            <DashboardSection
              title="Discovery map"
              eyebrow="Verified nodes, activity density, nearby signals"
              action={<Button variant="outline" size="sm" leftIcon={<Map size={15} />} onClick={() => setMapExpanded((value) => !value)}>{mapExpanded ? 'Compact map' : 'Expand map'}</Button>}
            >
              <div ref={mapGate.ref}>
              {mapGate.inView ? <DirectoryLiveMap
                records={mapRecords}
                hoveredSlug={hoveredSlug}
                onHover={setHoveredSlug}
                onLocate={(lat, lng) => { setNearby({ lat, lng }); setPage(1) }}
                height={mapExpanded ? 560 : 380}
                headerLabel={`${city === 'All Cities' ? 'All India' : city} coverage`}
                headerChips={[
                  { k: 'on map', v: String(mapRecords.length) },
                  { k: 'verified', v: String(verifiedCount) },
                  { k: 'in view', v: String(filtered.length) },
                ]}
                nextPrayer={nextPrayer}
              /> : <div style={{ height: mapExpanded ? 560 : 380, borderRadius: 18, background: 'linear-gradient(160deg,#101A38,#0B132B)' }} />}
              </div>
            </DashboardSection>

            <div className="directory-toolbar" role="toolbar" aria-label="Directory filters">
              {SECT_FACET_ENABLED && (
                <select
                  className="directory-tb-select"
                  value={sect}
                  onChange={(e) => { setSect(e.target.value); setPage(1) }}
                  aria-label="Filter by sect"
                >
                  {SECTS.map((item) => <option key={item} value={item}>{item === 'All' ? 'Sect: All' : item}</option>)}
                </select>
              )}

              <select
                className="directory-tb-select"
                value={tier}
                onChange={(e) => { setTier(e.target.value as 'All' | 'Verified' | 'Listed'); setPage(1) }}
                aria-label="Filter by listing tier"
              >
                <option value="All">Tier: All</option>
                <option value="Verified">Verified</option>
                <option value="Listed">Listed (OSM)</option>
              </select>

              <div className="directory-tb-multi">
                <button type="button" className="directory-tb-trigger" onClick={() => setFacilityMenuOpen((v) => !v)} aria-expanded={facilityMenuOpen}>
                  Facilities{facilityFilters.length > 0 && <b>{facilityFilters.length}</b>} <ChevronDown size={13} strokeWidth={2} />
                </button>
                {facilityMenuOpen && (
                  <div className="directory-tb-menu">
                    {FILTERS.map((item) => (
                      <label key={item.id}>
                        <input
                          type="checkbox"
                          checked={facilityFilters.includes(item.id)}
                          onChange={() => toggleFacility(item.id)}
                        />
                        {item.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <Button variant="outline" size="sm" leftIcon={<Filter size={15} />} onClick={() => setShowFilterDrawer(true)}>More filters</Button>
              {(sect !== 'All' || tier !== 'All' || facilityFilters.length > 0 || search) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>
              )}
            </div>
            {(sect !== 'All' || tier !== 'All' || facilityFilters.length > 0 || nearby) && (
              <div className="directory-active-chips">
                {sect !== 'All' && (
                  <button type="button" onClick={() => { setSect('All'); setPage(1) }}>{sect} ✕</button>
                )}
                {tier !== 'All' && (
                  <button type="button" onClick={() => { setTier('All'); setPage(1) }}>{tier === 'Listed' ? 'Listed (OSM)' : tier} ✕</button>
                )}
                {facilityFilters.map((f) => (
                  <button key={f} type="button" onClick={() => toggleFacility(f)}>{FILTERS.find((x) => x.id === f)?.label || f} ✕</button>
                ))}
                {nearby && (
                  <button type="button" onClick={() => { setNearby(null); setPage(1) }}>Within {NEARBY_RADIUS_KM}km ✕</button>
                )}
              </div>
            )}

            <div className="directory-open-panel">
              <DashboardSection
                title="Trusted masjid directory"
                eyebrow={`${total.toLocaleString('en-IN')} masjids${total !== totalAll ? ` of ${totalAll.toLocaleString('en-IN')}` : ''}${city !== 'All Cities' ? ` in ${city}` : ''}${nearby ? ` · within ${NEARBY_RADIUS_KM}km` : ''} — ${verifiedCount} verified`}
                action={<Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>}
              >
                {error && <AIAlert title="Directory fallback active" body={error} tone="warning" />}

                {loading ? (
                  <GridSystem columns={3}>
                    {Array.from({ length: 6 }).map((_, index) => (
                      <PremiumCard key={index} className="directory-skeleton-card">
                        <span />
                        <span />
                        <span />
                      </PremiumCard>
                    ))}
                  </GridSystem>
                ) : paginated.length ? (
                  <div className="directory-kit-grid">
                    {paginated.flatMap((masjid, idx) => [
                    /* FS2-D: fluid CARD creative every 9 results, max 2/page — height:100%
                       so the grid's align-items:stretch equalizes it with neighbouring cards. */
                    ...(idx > 0 && idx % 9 === 0 && idx / 9 <= 2
                      ? [<AdSlot key={`ad-${idx}`} placement="directory_grid" variant="card" city={city === 'All Cities' ? undefined : city} />]
                      : []),
                    ((masjid) => (
                      <div
                        key={masjid.id || masjid.slug}
                        onMouseEnter={() => setHoveredSlug(masjid.slug)}
                        onMouseLeave={() => setHoveredSlug(null)}
                        className={hoveredSlug === masjid.slug ? 'directory-kit-cell is-hot' : 'directory-kit-cell'}
                      >
                        <MasjidKitCard
                          m={{ id: masjid.id, slug: masjid.slug, name: masjid.nameEnglish, area: masjid.district, city: masjid.city, verified: masjid.verified, coverPhoto: masjid.coverPhoto || (masjid as any).photo, photoReady: masjid.photoReady, capacity: masjid.capacity, facilities: masjid.facilities , importSource: masjid.importSource }}
                          nextPrayer={nextPrayer}
                        />
                      </div>
                    ))(masjid),
                  ])}
                  </div>
                ) : (
                  <PremiumCard className="directory-empty-state">
                    <Search size={24} />
                    <h3>No masjids matched this search</h3>
                    <p>Try a broader area, fewer filters, or add the institution so the community can verify it.</p>
                    <ButtonLink href="/onboard">Add masjid</ButtonLink>
                  </PremiumCard>
                )}

                {!loading && totalPages > 1 && (
                  <div className="directory-pagination" style={{ justifyContent: 'center' }}>
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</Button>
                    <span>Page {page} of {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>Next</Button>
                  </div>
                )}
              </DashboardSection>
            </div>

            <div className="directory-open-panel">
              <DashboardSection title="Coverage snapshot" eyebrow="Live counts from the current results">
                <GridSystem columns={4}>
                  <PremiumMetricCard label="Masjids in view" value={String(filtered.length)} trend="Matching your filters" />
                  <PremiumMetricCard label="Verified" value={String(verifiedCount)} trend="Committee confirmed" tone="gold" />
                  <PremiumMetricCard label="Education facilities" value={String(educationCount)} trend="Qur'an and classes listed" />
                  <PremiumMetricCard label="Cities covered" value={String(new Set(filtered.map((m) => m.city).filter(Boolean)).size)} trend="In current results" />
                </GridSystem>
              </DashboardSection>
            </div>
          </div>

          <div className="directory-open-panel">
          <aside className="directory-rail">
            {/* The rail creative is now a fluid rectangle (skyscraper removed) — it fills
                the rail column at every breakpoint, so the 900–1120px mismatch is gone. */}
            <AdSlot placement="directory_rail" variant="sidebar" city={city === 'All Cities' ? undefined : city} />
            {nextPrayer && (
              <PremiumCard className="directory-rail-widget">
                <div className="drw-head"><Bell size={13} strokeWidth={1.5} /> NEXT SALAH · {(city === 'All Cities' ? 'Hyderabad' : city).toUpperCase()}</div>
                <div className="drw-prayer">
                  <strong>{nextPrayer.name}</strong>
                  <span>{nextPrayer.time}</span>
                </div>
                <p className="drw-note">Calculated times (Aladhan) — jamaat schedules are confirmed by each masjid&rsquo;s committee.</p>
              </PremiumCard>
            )}

            <PremiumCard className="directory-rail-widget">
              <div className="drw-head"><MessageCircle size={13} strokeWidth={1.5} /> FROM THE UMMAH</div>
              <UmmahRail />
            </PremiumCard>

            <PremiumCard className="directory-rail-widget">
              <DirectoryGlance city={city} />
            </PremiumCard>

            {/* Sprint 5 — compact Collections control: 6 group headings (never a long flat list) */}
            <PremiumCard className="directory-rail-widget">
              <div className="drw-head"><LayoutGrid size={13} strokeWidth={1.5} /> COLLECTIONS</div>
              <div className="drw-collections">
                {COLLECTION_GROUPS.map((g) => (
                  <Link key={g.slug} href={`/masjids/collections/${g.slug}`}>{g.title}</Link>
                ))}
                <Link href="/masjids/shaheed">Shaheed Masajid</Link>
              </div>
              <Link className="drw-collections-all" href="/masjids/collections">Browse all collections →</Link>
            </PremiumCard>

            <PremiumCard className="directory-rail-widget">
              <div className="drw-head"><Filter size={13} strokeWidth={1.5} /> IN THIS VIEW</div>
              <div className="drw-rows">
                <div><i>Results</i><b>{filtered.length}</b></div>
                <div><i>Verified by committees</i><b>{verifiedCount}</b></div>
                <div><i>Listed from OpenStreetMap</i><b>{filtered.length - verifiedCount}</b></div>
                {educationCount > 0 && <div><i>With education programs</i><b>{educationCount}</b></div>}
              </div>
              <p className="drw-note">Listed masjids show basic public data until their committee claims the profile.</p>
            </PremiumCard>

            {fresh?.newThisWeek?.count > 0 && (
              <PremiumCard className="directory-rail-widget">
                <div className="drw-head"><Sparkles size={13} strokeWidth={1.5} /> NEW THIS WEEK</div>
                <div className="drw-big">{fresh.newThisWeek.count.toLocaleString('en-IN')}<span> masjids added</span></div>
                <div className="drw-links">
                  {fresh.newThisWeek.items.slice(0, 3).map((m: any) => (
                    <Link key={m.slug} href={`/masjids/${m.slug}`}>{m.name} <em>{m.city || 'India'}</em></Link>
                  ))}
                </div>
              </PremiumCard>
            )}

            {topCities.length > 0 && (
              <PremiumCard className="directory-rail-widget">
                <div className="drw-head"><MapPin size={13} strokeWidth={1.5} /> TOP CITIES ON THE NETWORK</div>
                <div className="drw-cities">
                  {topCities.map((c) => (
                    <button key={c.city} type="button" className={city === c.city ? 'on' : ''} onClick={() => { setCity(c.city); setPage(1) }}>
                      {c.city} <b>{c.n.toLocaleString('en-IN')}</b>
                    </button>
                  ))}
                </div>
              </PremiumCard>
            )}

            <PremiumCard surface="dark" className="directory-add-card">
              <ShieldCheck size={20} />
              <h3>Can&apos;t find a masjid?</h3>
              <p>Add it in minutes and help the community build a trusted institutional map.</p>
              <ButtonLink href="/onboard" variant="gold">Add masjid</ButtonLink>
            </PremiumCard>
          </aside>
          </div>
        </div>
      </SectionContainer>

      <button className="directory-floating-map" type="button" onClick={() => setMapExpanded((value) => !value)}>
        <Map size={17} /> Map
      </button>

      <div className={showFilterDrawer ? 'directory-filter-drawer is-open' : 'directory-filter-drawer'} aria-hidden={!showFilterDrawer}>
        <button className="directory-filter-backdrop" type="button" onClick={() => setShowFilterDrawer(false)} aria-label="Close filters" />
        <div className="directory-filter-sheet">
          <div className="directory-sheet-head">
            <div>
              <span>Smart filters</span>
              <strong>Refine discovery</strong>
            </div>
            <Button size="icon" variant="ghost" onClick={() => setShowFilterDrawer(false)} aria-label="Close filters"><X size={18} /></Button>
          </div>
          <label className="directory-select-field">
            <span>City</span>
            <select value={city} onChange={(event) => { setCity(event.target.value); setPage(1) }}>
              {CITIES.map((item) => <option key={item}>{item}</option>)}
            </select>
          </label>
          {/* Lifecycle view — functioning masajid (default) / dormant / shaheed (remembrance).
              Dignified copy; the word "Active" is never used as a public label. */}
          <div className="directory-filter-group" role="group" aria-label="Masjid lifecycle view">
            {([['masjid', 'Masajid'], ['dormant', 'Dormant'], ['shaheed', 'Shaheed']] as const).map(([value, label]) => (
              <FilterChipButton key={value} label={label} active={classification === value} onClick={() => { setClassification(value); setPage(1) }} />
            ))}
          </div>
          {SECT_FACET_ENABLED && (
            <div className="directory-filter-group">
              {SECTS.map((item) => (
                <FilterChipButton key={item} icon={item === 'All' ? LayoutGrid : Building2} label={item} active={sect === item} onClick={() => { setSect(item); setPage(1) }} />
              ))}
            </div>
          )}
          <div className="directory-filter-group">
            {FILTERS.map((item) => (
              <FilterChipButton key={item.id} icon={item.icon} label={item.label} active={facilityFilters.includes(item.id)} onClick={() => toggleFacility(item.id)} />
            ))}
          </div>
          {/* Prayer-time filter/sort — DARK behind prayerTimeFilter. COMMITTEE-CONFIRMED times only;
              the server enforces confirmed-only and never ranks Aladhan estimates. */}
          {prayerFilterOn && (
            <div className="directory-filter-group" role="group" aria-label="Prayer-time filters" style={{ borderTop: '1px solid rgba(11,19,43,0.08)', paddingTop: 12, marginTop: 4 }}>
              <div style={{ width: '100%', fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 2 }}>Prayer times</div>
              <div style={{ width: '100%', fontSize: 11, color: '#94A3B8', marginBottom: 6 }}>Only masjids with committee-confirmed times — estimated (city-calculated) times are never ranked.</div>
              <FilterChipButton icon={Clock} label="Confirmed prayer times only" active={confirmedPrayerOnly} onClick={() => { setConfirmedPrayerOnly((v) => !v); setPage(1) }} />
              {(['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'] as const).map((p) => (
                <FilterChipButton key={p} label={`Sort: ${p.charAt(0).toUpperCase() + p.slice(1)}`} active={prayerSort === p} onClick={() => { setPrayerSort((cur) => (cur === p ? '' : p)); setPage(1) }} />
              ))}
              {prayerSort && (
                <>
                  <FilterChipButton label="Earliest first" active={prayerSortDir === 'asc'} onClick={() => { setPrayerSortDir('asc'); setPage(1) }} />
                  <FilterChipButton label="Latest first" active={prayerSortDir === 'desc'} onClick={() => { setPrayerSortDir('desc'); setPage(1) }} />
                </>
              )}
            </div>
          )}
          <Button onClick={() => setShowFilterDrawer(false)}>Apply filters</Button>
        </div>
      </div>


      <style jsx global>{`
        .directory-premium-page {
          
          overflow-x: hidden;
          background: linear-gradient(180deg, var(--color-ivory-50), var(--color-ivory-200) 42%, var(--color-ivory-50));
          color: var(--color-navy-900);
        }
        .directory-hero {
          border-bottom: 1px solid var(--border-light);
          background:
            radial-gradient(circle at 18% 16%, rgba(14,124,97,0.1), transparent 28%),
            radial-gradient(circle at 88% 26%, rgba(212,175,55,0.12), transparent 26%),
            linear-gradient(180deg, rgba(255,255,255,0.96), rgba(251,248,239,0.78));
        }
        .directory-hero-inner {
          display: grid;
          grid-template-columns: minmax(0, 0.8fr) minmax(420px, 1.2fr);
          align-items: end;
          gap: clamp(28px, 5vw, 72px);
          padding-block: clamp(42px, 6vw, 76px);
        }
        .directory-hero-inner > * {
          min-width: 0;
        }
        .directory-eyebrow,
        .directory-card-kicker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: var(--color-emerald-700);
          font-size: 12px;
          font-weight: 900;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .directory-hero-copy h1 {
          max-width: 620px;
          margin: 16px 0 0;
          font-family: var(--font-display);
          font-size: clamp(2.45rem, 4.4vw, 4.25rem);
          line-height: 1.04;
          letter-spacing: 0;
        }
        .directory-hero-copy p {
          max-width: 650px;
          margin: 18px 0 0;
          color: var(--text-secondary);
          font-size: clamp(1rem, 1.3vw, 1.18rem);
          line-height: 1.72;
        }
        .directory-search-panel {
          display: grid;
          gap: 14px;
        }
        .directory-ai-search {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 62px;
          padding: 8px 8px 8px 16px;
          border: 1px solid rgba(14,124,97,0.22);
          border-radius: var(--radius-lg);
          background: linear-gradient(180deg, #fff, var(--color-ivory-50));
          box-shadow: var(--shadow-sm);
        }
        .directory-ai-search svg {
          flex: 0 0 auto;
          color: var(--color-emerald-600);
        }
        .directory-ai-search input {
          min-width: 0;
          flex: 1;
          border: 0;
          outline: 0;
          background: transparent;
          color: var(--color-navy-900);
          font: inherit;
          font-size: 15px;
        }
        /* rail widgets (D3) */
        .directory-rail :global(.directory-rail-widget) { display: block; padding: 15px 17px; }
        .drw-head { display: flex; align-items: center; gap: 6px; font-family: var(--f-mono); font-size: 9px; letter-spacing: .13em; color: var(--f-green); font-weight: 600; margin-bottom: 10px; }
        .drw-collections { display: flex; flex-direction: column; gap: 2px; }
        .drw-collections a { font-size: 12.5px; font-weight: 600; color: var(--f-navy); text-decoration: none; padding: 6px 8px; border-radius: 8px; }
        .drw-collections a:hover { background: rgba(14,124,97,.07); color: var(--f-green-deep); }
        .drw-collections-all { display: inline-block; margin-top: 8px; font-size: 11.5px; font-weight: 600; color: var(--f-green); text-decoration: none; }
        .drw-prayer { display: flex; align-items: baseline; gap: 10px; }
        .drw-prayer strong { font-family: var(--f-display); font-size: 22px; font-weight: 700; color: var(--f-navy); }
        .drw-prayer span { font-family: var(--f-mono); font-size: 14px; color: var(--f-green-deep); font-weight: 600; }
        .drw-note { font-size: 10.5px; color: var(--f-slate-dim); line-height: 1.5; margin-top: 9px; }
        .drw-rows { display: grid; gap: 7px; }
        .drw-rows > div { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px dashed var(--f-line); padding-bottom: 6px; }
        .drw-rows > div:last-child { border-bottom: none; }
        .drw-rows i { font-style: normal; font-size: 11.5px; color: var(--f-slate); }
        .drw-rows b { font-family: var(--f-mono); font-size: 12.5px; color: var(--f-navy); }
        .drw-big { font-family: var(--f-display); font-size: 24px; font-weight: 700; color: var(--f-navy); line-height: 1.1; }
        .drw-big span { display: block; font-family: inherit; font-size: 11px; font-weight: 500; color: var(--f-slate); margin-top: 2px; }
        .drw-links { display: grid; gap: 6px; margin-top: 10px; }
        .drw-links :global(a) { font-size: 12px; font-weight: 600; color: var(--f-navy); text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .drw-links :global(a:hover) { color: var(--f-green); }
        .drw-links :global(a em) { font-style: normal; font-weight: 400; font-size: 10.5px; color: var(--f-slate-dim); margin-left: 5px; }
        .drw-cities { display: flex; flex-wrap: wrap; gap: 6px; }
        .drw-cities button { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--f-line-strong); border-radius: 99px; background: #fff; color: var(--f-slate); font-size: 11px; font-weight: 600; padding: 5px 11px; cursor: pointer; font-family: inherit; transition: all .2s; }
        .drw-cities button:hover { border-color: rgba(14,124,97,.35); color: var(--f-green-deep); }
        .drw-cities button.on { background: rgba(14,124,97,.08); border-color: rgba(14,124,97,.35); color: var(--f-green-deep); }
        .drw-cities button b { font-family: var(--f-mono); font-size: 10px; color: var(--f-green); }
        .directory-smart-read { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; }
        .directory-smart-chip { display: inline-flex; align-items: center; font-size: 11px; font-weight: 600; color: var(--f-green-deep); background: rgba(14,124,97,.08); border: 1px solid rgba(14,124,97,.22); border-radius: 99px; padding: 4px 11px; }
        .directory-smart-read > button { border: none; background: none; color: var(--f-slate-dim); font-size: 11px; cursor: pointer; font-family: inherit; text-decoration: underline; padding: 2px 4px; }
        .directory-search-meta,
        .directory-trending,
        .directory-filter-group,
                /* One-line filter toolbar */
        .directory-toolbar { display: flex; align-items: center; gap: 9px; margin: 18px 0 6px; overflow-x: auto; padding-bottom: 2px; scrollbar-width: none; }
        .directory-toolbar::-webkit-scrollbar { display: none; }
        .directory-tb-select { appearance: none; border: 1px solid var(--f-line-strong); border-radius: 10px; background-color: #fff; padding: 9px 30px 9px 13px; font-size: 12.5px; font-weight: 500; color: var(--f-ink); font-family: inherit; cursor: pointer; background-image: url("data:image/svg+xml,%3Csvg xmlns='http:%2F%2Fwww.w3.org%2F2000%2Fsvg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%235E6A85' stroke-width='1.6' fill='none' stroke-linecap='round'%2F%3E%3C%2Fsvg%3E"); background-repeat: no-repeat; background-position: right 11px center; white-space: nowrap; }
        .directory-tb-multi { position: relative; }
        .directory-tb-trigger { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--f-line-strong); border-radius: 10px; background: #fff; padding: 9px 13px; font-size: 12.5px; font-weight: 500; color: var(--f-ink); font-family: inherit; cursor: pointer; white-space: nowrap; }
        .directory-tb-trigger b { background: var(--f-green); color: #fff; border-radius: 99px; font-size: 10px; min-width: 17px; height: 17px; display: grid; place-items: center; padding: 0 4px; }
        .directory-tb-menu { position: absolute; top: calc(100% + 6px); left: 0; z-index: 60; min-width: 210px; background: #fff; border: 1px solid var(--f-line); border-radius: 13px; box-shadow: var(--f-shadow-lg); padding: 8px; display: grid; gap: 2px; }
        .directory-tb-menu label { display: flex; align-items: center; gap: 9px; font-size: 12.5px; color: var(--f-ink); padding: 7px 9px; border-radius: 8px; cursor: pointer; }
        .directory-tb-menu label:hover { background: rgba(14, 124, 97, .06); }
        .directory-tb-menu input { accent-color: var(--f-green); }
        .directory-active-chips { display: flex; gap: 7px; flex-wrap: wrap; margin: 0 0 6px; }
        .directory-active-chips button { display: inline-flex; align-items: center; gap: 5px; border: 1px solid rgba(14, 124, 97, .3); border-radius: 99px; background: rgba(14, 124, 97, .07); color: var(--f-green-deep); font-size: 11.5px; font-weight: 500; padding: 5px 11px; cursor: pointer; font-family: inherit; }

        /* 1R-2: uniform listings grid — 3/2/1, equal heights */
        .directory-kit-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; align-items: stretch; }
        .directory-kit-cell { display: flex; flex-direction: column; border-radius: 16px; transition: box-shadow .2s; }
        .directory-kit-cell > :global(*) { flex: 1; }
        .directory-kit-cell.is-hot { box-shadow: 0 0 0 2px rgba(20, 184, 166, .55), 0 14px 38px rgba(11, 19, 43, .14); }
        @media (max-width: 1080px) { .directory-kit-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 640px) { .directory-kit-grid { grid-template-columns: 1fr; } }

        .directory-pagination {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .directory-search-meta {
          align-items: center;
          color: var(--text-muted);
          font-size: 12px;
          font-weight: 800;
        }
        .directory-search-meta button,
        .directory-trending button {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 0;
          background: transparent;
          color: var(--color-emerald-700);
          font: inherit;
          font-weight: 900;
          cursor: pointer;
        }
        .directory-trending button {
          padding: 8px 11px;
          border: 1px solid var(--border-light);
          border-radius: 999px;
          background: white;
          color: var(--color-navy-700);
          font-size: 12px;
        }
        .directory-sticky-search {
          display: none;
          position: sticky;
          top: 0;
          z-index: 40;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-light);
          background: rgba(254,253,248,0.86);
          backdrop-filter: blur(18px);
        }
        .directory-sticky-shell {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          border: 1px solid var(--border-light);
          border-radius: var(--radius-lg);
          background: white;
        }
        .directory-sticky-shell input {
          min-width: 0;
          flex: 1;
          border: 0;
          outline: 0;
          background: transparent;
          font: inherit;
        }
        .directory-main-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 340px;
          align-items: start;
          gap: 22px;
        }
        .directory-primary,
        .directory-rail {
          display: grid;
          gap: 18px;
          min-width: 0;
        }
        .directory-progressive-panel {
          border: 1px solid var(--border-light);
          border-radius: var(--radius-xl);
          background: rgba(255,255,255,0.76);
          box-shadow: var(--shadow-xs);
        }
        .directory-progressive-panel summary {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 15px 18px;
          cursor: pointer;
          list-style: none;
        }
        .directory-progressive-panel summary::-webkit-details-marker {
          display: none;
        }
        .directory-progressive-panel summary span {
          color: var(--color-navy-900);
          font-size: 15px;
          font-weight: 950;
        }
        .directory-progressive-panel summary small {
          color: var(--text-muted);
          font-size: 12px;
          font-weight: 800;
          text-align: right;
        }
        .directory-progressive-panel[open] {
          padding-bottom: 12px;
        }
        .directory-progressive-panel[open] summary {
          border-bottom: 1px solid var(--border-light);
        }
        .directory-filter-disclosure-body {
          padding: 14px;
        }
        .directory-filter-disclosure .directory-filter-system {
          gap: 12px;
        }
        .directory-rail-panel {
          align-self: start;
        }
        .directory-rail-panel .directory-rail {
          position: static;
          padding: 14px;
        }
        .directory-rail {
          position: sticky;
          top: 86px;
        }
        .directory-map-wrap {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 310px;
          gap: 14px;
          min-width: 0;
        }
        .directory-map-wrap.is-expanded {
          grid-template-columns: 1fr;
        }
        .directory-map-surface {
          position: relative;
          min-height: 470px;
          overflow: hidden;
          border-radius: var(--radius-xl);
          border: 1px solid rgba(255,255,255,0.12);
          background:
            radial-gradient(circle at 48% 50%, rgba(14,124,97,0.28), transparent 38%),
            linear-gradient(145deg, var(--color-navy-900), #0f2232 58%, #13231e);
          box-shadow: var(--shadow-lg);
        }
        .directory-map-grid {
          position: absolute;
          inset: 0;
          opacity: 0.26;
          background-image:
            linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px);
          background-size: 44px 44px;
        }
        .directory-map-density {
          position: absolute;
          border-radius: 999px;
          filter: blur(8px);
        }
        .directory-density-a {
          left: 22%;
          top: 24%;
          width: 220px;
          height: 220px;
          background: rgba(14,124,97,0.2);
        }
        .directory-density-b {
          right: 12%;
          bottom: 13%;
          width: 250px;
          height: 250px;
          background: rgba(212,175,55,0.14);
        }
        .directory-map-lines {
          position: absolute;
          inset: 7%;
          width: 86%;
          height: 86%;
          overflow: visible;
        }
        .directory-map-lines path {
          fill: none;
          stroke: rgba(168,217,204,0.4);
          stroke-width: 1.5;
          stroke-dasharray: 7 9;
        }
        .directory-map-node {
          position: absolute;
          border-radius: 999px;
          background: var(--color-gold-300);
          box-shadow: 0 0 0 8px rgba(212,175,55,0.13), 0 0 24px rgba(212,175,55,0.72);
          animation: directoryPulse 3.4s ease-in-out infinite;
        }
        .directory-map-node span {
          position: absolute;
          left: 16px;
          top: -10px;
          min-width: max-content;
          padding: 5px 8px;
          border-radius: 999px;
          background: rgba(255,255,255,0.9);
          color: var(--color-navy-800);
          font-size: 11px;
          font-weight: 900;
          opacity: 0;
          transform: translateY(4px);
          transition: var(--motion-fast);
        }
        .directory-map-node:hover span {
          opacity: 1;
          transform: translateY(0);
        }
        .directory-map-card {
          position: absolute;
          left: 18px;
          bottom: 18px;
          width: min(330px, calc(100% - 36px));
          padding: 16px;
          border: 1px solid rgba(255,255,255,0.16);
          border-radius: var(--radius-lg);
          background: rgba(255,255,255,0.1);
          color: white;
          backdrop-filter: blur(20px);
        }
        .directory-map-card strong {
          display: block;
          margin-top: 10px;
          font-size: 20px;
        }
        .directory-map-card p {
          margin: 6px 0 0;
          color: rgba(255,255,255,0.7);
          line-height: 1.6;
        }
        .directory-nearby-panel,
        .directory-location-card,
        .directory-community-card,
        .directory-add-card {
          display: grid;
          gap: 12px;
        }
        .directory-nearby-panel {
          padding: 18px;
          border: 1px solid var(--border-light);
          border-radius: var(--radius-xl);
          background: white;
        }
        .directory-nearby-panel > span,
        .directory-location-card span,
        .directory-sheet-head span,
        .directory-select-field span {
          color: var(--text-muted);
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .directory-nearby-panel h3 {
          margin: 0;
          font-size: 18px;
          line-height: 1.25;
        }
        .directory-filter-system {
          display: grid;
          gap: 12px;
        }
        /* Pillar chips — brand-token treatment so Shaheed/Adopt read as real destinations.
           Shaheed leans navy/gold (the archive's palette); Adopt leans emerald/gold. Hover
           inverts to each pillar's page palette. */
        .directory-pillar-row { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin: 10px 0 2px; }
        .directory-pillar-label { font-size: 13px; font-weight: 600; color: var(--color-navy-500); }
        .directory-pillar-chip {
          display: inline-flex; align-items: center; gap: 7px;
          border-radius: 999px; padding: 8px 16px;
          font-size: 12.5px; font-weight: 700; text-decoration: none;
          border: 1px solid; box-shadow: var(--shadow-xs);
          transition: background .16s ease, color .16s ease, border-color .16s ease, transform .16s ease;
        }
        .directory-pillar-chip:hover { transform: translateY(-1px); }
        .directory-pillar-chip.is-shaheed {
          color: var(--color-navy-800); background: var(--color-navy-50); border-color: var(--color-gold-400);
        }
        .directory-pillar-chip.is-shaheed:hover {
          background: var(--color-navy-900); color: var(--color-gold-300); border-color: var(--color-gold-500);
        }
        .directory-pillar-chip.is-adopt {
          color: var(--color-emerald-700); background: var(--color-emerald-50); border-color: var(--color-emerald-300);
        }
        .directory-pillar-chip.is-adopt:hover {
          background: var(--color-emerald-700); color: #fff; border-color: var(--color-gold-400);
        }
        .directory-filter-chip {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 9px 12px;
          border: 1px solid var(--border-light);
          border-radius: 999px;
          background: white;
          color: var(--color-navy-700);
          font: inherit;
          font-size: 13px;
          font-weight: 850;
          cursor: pointer;
          transition: var(--motion-fast);
        }
        .directory-filter-chip:hover,
        .directory-filter-chip.is-active {
          border-color: rgba(14,124,97,0.28);
          background: var(--color-emerald-50);
          color: var(--color-emerald-700);
          box-shadow: var(--shadow-xs);
        }
        .directory-card-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
        }
        .directory-masjid-card {
          display: grid;
          gap: 14px;
          padding: 16px;
          border: 1px solid var(--border-light);
          border-radius: var(--radius-xl);
          background:
            linear-gradient(180deg, rgba(255,255,255,0.96), rgba(254,253,248,0.94));
          color: inherit;
          text-decoration: none;
          box-shadow: var(--shadow-xs);
          transition: var(--motion-normal);
        }
        .directory-masjid-card:hover {
          transform: translateY(-2px);
          border-color: rgba(14,124,97,0.26);
          box-shadow: var(--shadow-md);
        }
        .directory-card-topline {
          display: flex;
          justify-content: space-between;
          gap: 12px;
        }
        .directory-card-topline h3 {
          margin: 0;
          color: var(--color-navy-900);
          font-size: 18px;
          line-height: 1.25;
        }
        .directory-card-topline p {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          margin: 6px 0 0;
          color: var(--text-muted);
          font-size: 13px;
          line-height: 1.4;
        }
        .directory-card-metrics {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
        }
        .directory-card-metrics div {
          min-width: 0;
          padding: 10px;
          border: 1px solid var(--border-light);
          border-radius: var(--radius-md);
          background: white;
        }
        .directory-card-metrics span,
        .directory-card-metrics small {
          display: block;
          color: var(--text-muted);
          font-size: 10px;
          font-weight: 850;
        }
        .directory-card-metrics strong {
          display: block;
          margin: 4px 0 2px;
          color: var(--color-navy-900);
          font-size: 15px;
          line-height: 1.15;
        }
        .directory-facility-row {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }
        .directory-facility-row span {
          padding: 6px 8px;
          border-radius: 999px;
          background: var(--color-ivory-200);
          color: var(--color-navy-700);
          font-size: 11px;
          font-weight: 850;
        }
        .directory-ai-note {
          display: flex;
          gap: 9px;
          padding: 11px;
          border: 1px solid rgba(14,124,97,0.14);
          border-radius: var(--radius-md);
          background: var(--color-emerald-50);
          color: var(--color-emerald-800);
          font-size: 12px;
          font-weight: 700;
          line-height: 1.55;
        }
        .directory-ai-note svg {
          flex: 0 0 auto;
          margin-top: 2px;
        }
        .directory-card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          color: var(--text-muted);
          font-size: 12px;
          font-weight: 800;
        }
        .directory-card-footer b {
          padding: 4px 8px;
          border-radius: 999px;
          font-size: 10px;
          text-transform: uppercase;
        }
        .directory-skeleton-card {
          display: grid;
          gap: 12px;
        }
        .directory-skeleton-card span {
          height: 18px;
          border-radius: 999px;
          background: linear-gradient(90deg, var(--color-ivory-200), white, var(--color-ivory-200));
          background-size: 200% 100%;
          animation: directoryShimmer 1.6s infinite;
        }
        .directory-empty-state {
          display: grid;
          justify-items: center;
          gap: 10px;
          padding: 38px;
          text-align: center;
        }
        .directory-pagination {
          align-items: center;
          justify-content: center;
          margin-top: 18px;
          color: var(--text-muted);
          font-size: 13px;
          font-weight: 850;
        }
        .directory-location-card {
          grid-template-columns: auto 1fr;
        }
        .directory-location-card svg,
        .directory-community-card svg {
          color: var(--color-emerald-600);
        }
        .directory-location-card strong {
          display: block;
          margin-top: 3px;
          color: var(--color-navy-900);
          font-size: 18px;
        }
        .directory-location-card p,
        .directory-community-card span,
        .directory-add-card p {
          margin: 0;
          color: var(--text-secondary);
          line-height: 1.6;
          font-size: 13px;
        }
        .directory-community-card .premium-mobile-stack > div {
          display: flex;
          align-items: flex-start;
          gap: 9px;
          padding: 10px 0;
          border-top: 1px solid var(--border-light);
        }
        .directory-add-card h3 {
          color: white;
        }
        .directory-floating-map {
          display: none;
          position: fixed;
          right: 18px;
          bottom: 92px;
          z-index: 45;
          align-items: center;
          gap: 7px;
          padding: 12px 15px;
          border: 0;
          border-radius: 999px;
          background: var(--color-navy-900);
          color: white;
          font: inherit;
          font-weight: 900;
          box-shadow: var(--shadow-lg);
        }
        .directory-filter-drawer {
          pointer-events: none;
          position: fixed;
          inset: 0;
          z-index: 80;
          opacity: 0;
          transition: var(--motion-normal);
        }
        .directory-filter-drawer.is-open {
          pointer-events: auto;
          opacity: 1;
        }
        .directory-filter-backdrop {
          position: absolute;
          inset: 0;
          border: 0;
          background: rgba(11,19,43,0.36);
        }
        .directory-filter-sheet {
          position: absolute;
          right: 0;
          bottom: 0;
          left: 0;
          display: grid;
          gap: 16px;
          padding: 18px;
          border-radius: 24px 24px 0 0;
          background: var(--color-ivory-50);
          box-shadow: var(--shadow-lg);
          transform: translateY(18px);
          transition: var(--motion-normal);
        }
        .directory-filter-drawer.is-open .directory-filter-sheet {
          transform: translateY(0);
        }
        .directory-sheet-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
        }
        .directory-sheet-head strong {
          display: block;
          margin-top: 3px;
          font-size: 20px;
        }
        .directory-select-field {
          display: grid;
          gap: 7px;
        }
        .directory-select-field select {
          width: 100%;
          padding: 12px;
          border: 1px solid var(--border-light);
          border-radius: var(--radius-md);
          background: white;
          font: inherit;
        }
        /* Final directory convergence: richer map, locality intelligence, premium card rhythm. */
        .directory-premium-page {
          background:
            radial-gradient(circle at 9% 2%, rgba(14,124,97,0.12), transparent 25%),
            radial-gradient(circle at 88% 5%, rgba(212,175,55,0.13), transparent 26%),
            linear-gradient(180deg, #fffaf0 0%, #f7f1e4 48%, #fffdfa 100%) !important;
        }
        .directory-hero {
          position: relative;
          overflow: hidden;
          border-bottom: 0 !important;
          background:
            radial-gradient(circle at 15% 18%, rgba(14,124,97,0.16), transparent 30%),
            radial-gradient(circle at 84% 20%, rgba(212,175,55,0.14), transparent 28%),
            linear-gradient(180deg, rgba(255,255,255,0.9), rgba(251,248,239,0.64)) !important;
        }
        .directory-hero::before {
          content: "";
          position: absolute;
          inset: 0;
          opacity: 0.46;
          pointer-events: none;
          background-image:
            linear-gradient(rgba(14,124,97,0.055) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14,124,97,0.045) 1px, transparent 1px);
          background-size: 42px 42px;
          mask-image: linear-gradient(115deg, rgba(0,0,0,0.62), transparent 74%);
        }
        .directory-hero-inner {
          position: relative;
          z-index: 1;
          grid-template-columns: minmax(0, 0.86fr) minmax(440px, 1fr) !important;
          align-items: center !important;
          padding-block: 38px 42px !important;
        }
        .directory-hero-copy h1 {
          max-width: 620px !important;
          font-size: clamp(2.65rem, 4.4vw, 4.35rem) !important;
          line-height: 1.01 !important;
          text-shadow: 0 18px 48px rgba(11,19,43,0.08);
        }
        .directory-hero-copy p {
          max-width: 590px !important;
          margin-top: 14px !important;
          line-height: 1.62 !important;
        }
        .directory-hero-signal-strip {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 18px;
        }
        .directory-hero-signal-strip span {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 8px 10px;
          border: 1px solid rgba(14,124,97,0.15);
          border-radius: 999px;
          background: rgba(255,255,255,0.78);
          color: #0b4f43;
          font-size: 12px;
          font-weight: 900;
          box-shadow: 0 12px 28px rgba(11,19,43,0.05);
          backdrop-filter: blur(16px);
        }
        .directory-search-panel {
          position: relative;
          overflow: hidden !important;
          padding: 20px !important;
          border-radius: 26px !important;
          border-color: rgba(14,124,97,0.16) !important;
          background:
            radial-gradient(circle at 10% 0%, rgba(14,124,97,0.15), transparent 32%),
            radial-gradient(circle at 95% 16%, rgba(212,175,55,0.18), transparent 30%),
            linear-gradient(145deg, rgba(255,255,255,0.94), rgba(255,250,240,0.76)) !important;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.8),
            0 24px 72px rgba(11,19,43,0.12) !important;
        }
        .directory-search-kicker {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          color: #0b4f43;
          font-size: 11px;
          font-weight: 950;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .directory-ai-search {
          min-height: 56px !important;
          border-radius: 18px !important;
          background: rgba(255,255,255,0.92) !important;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.82), 0 16px 36px rgba(11,19,43,0.08) !important;
        }
        .directory-trending button {
          border-color: rgba(14,124,97,0.13) !important;
          background: rgba(255,255,255,0.72) !important;
          box-shadow: 0 10px 24px rgba(11,19,43,0.045);
        }
        .directory-main-layout {
          gap: 18px !important;
        }
        .directory-primary {
          gap: 16px !important;
        }
        .directory-primary .premium-dashboard-section,
        .directory-rail .premium-card {
          border-color: rgba(14,124,97,0.12) !important;
          background:
            linear-gradient(180deg, rgba(255,255,255,0.94), rgba(255,250,240,0.82)),
            radial-gradient(circle at 96% 8%, rgba(212,175,55,0.1), transparent 24%) !important;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.74),
            0 18px 48px rgba(11,19,43,0.075) !important;
        }
        .directory-locality-strip {
          display: grid;
          grid-template-columns: minmax(0, 1fr) repeat(3, auto);
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
          padding: 12px;
          border: 1px solid rgba(14,124,97,0.12);
          border-radius: 18px;
          background: rgba(255,255,255,0.72);
          box-shadow: 0 14px 34px rgba(11,19,43,0.05);
        }
        .directory-locality-strip strong {
          display: block;
          color: #061527;
          font-size: 14px;
        }
        .directory-locality-strip span {
          display: block;
          margin-top: 2px;
          color: #64748b;
          font-size: 12px;
          font-weight: 800;
        }
        .directory-map-wrap {
          grid-template-columns: minmax(0, 1.08fr) 292px !important;
        }
        .directory-map-surface {
          min-height: 350px !important;
          border-radius: 28px !important;
          background:
            radial-gradient(circle at 50% 43%, rgba(25,201,148,0.34), transparent 34%),
            radial-gradient(circle at 76% 62%, rgba(244,215,137,0.18), transparent 28%),
            linear-gradient(145deg, #061527, #0a2232 58%, #10281f) !important;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.13),
            0 30px 82px rgba(7,18,37,0.23) !important;
        }
        .directory-map-noor,
        .directory-india-silhouette {
          position: absolute;
          pointer-events: none;
        }
        .directory-map-noor {
          inset: -20% -10% auto 34%;
          height: 58%;
          background: radial-gradient(circle, rgba(244,215,137,0.18), transparent 55%);
          filter: blur(6px);
        }
        .directory-india-silhouette {
          left: 29%;
          top: 20%;
          width: 34%;
          height: 54%;
          opacity: 0.42;
          border-radius: 52% 44% 56% 42% / 35% 48% 52% 66%;
          background:
            radial-gradient(circle at 48% 44%, rgba(25,201,148,0.45), transparent 46%),
            linear-gradient(150deg, rgba(168,217,204,0.22), rgba(244,215,137,0.12));
          box-shadow: 0 0 48px rgba(25,201,148,0.18);
          transform: rotate(-11deg);
        }
        .directory-map-hud {
          position: absolute;
          top: 18px;
          left: 18px;
          z-index: 3;
          width: min(300px, calc(100% - 36px));
          padding: 14px;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 18px;
          background: rgba(3,10,20,0.34);
          color: white;
          backdrop-filter: blur(20px);
          box-shadow: 0 18px 44px rgba(3,10,20,0.18);
        }
        .directory-map-hud span {
          color: rgba(255,250,240,0.66);
          font-size: 10px;
          font-weight: 950;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .directory-map-hud strong {
          display: block;
          margin-top: 4px;
          font-size: 16px;
        }
        .directory-map-hud div {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 10px;
          color: rgba(255,250,240,0.78);
          font-size: 11px;
          font-weight: 800;
        }
        .directory-map-hud b {
          color: #f4d789;
        }
        .directory-map-node {
          z-index: 2;
          background: #19c994 !important;
          box-shadow: 0 0 0 8px rgba(25,201,148,0.13), 0 0 28px rgba(25,201,148,0.74) !important;
        }
        .directory-map-node:nth-of-type(2n) {
          background: #f4d789 !important;
          box-shadow: 0 0 0 8px rgba(244,215,137,0.12), 0 0 26px rgba(244,215,137,0.62) !important;
        }
        .directory-map-card {
          z-index: 4;
          background: rgba(255,255,255,0.12) !important;
          box-shadow: 0 20px 52px rgba(3,10,20,0.22);
        }
        .directory-map-legend {
          position: absolute;
          right: 18px;
          bottom: 18px;
          z-index: 4;
          display: grid;
          gap: 8px;
          padding: 12px;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 16px;
          background: rgba(3,10,20,0.32);
          color: rgba(255,250,240,0.78);
          font-size: 11px;
          font-weight: 850;
          backdrop-filter: blur(18px);
        }
        .directory-map-legend span {
          display: flex;
          align-items: center;
          gap: 7px;
          white-space: nowrap;
        }
        .directory-map-legend i {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #19c994;
          box-shadow: 0 0 0 4px rgba(25,201,148,0.12);
        }
        .directory-map-legend span:nth-child(2) i {
          background: #f4d789;
          box-shadow: 0 0 0 4px rgba(244,215,137,0.12);
        }
        .directory-map-legend span:nth-child(3) i {
          background: #38bdf8;
          box-shadow: 0 0 0 4px rgba(56,189,248,0.12);
        }
        .directory-nearby-panel {
          border-color: rgba(14,124,97,0.14) !important;
          background:
            radial-gradient(circle at 100% 0%, rgba(244,215,137,0.14), transparent 30%),
            linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,250,240,0.84)) !important;
          box-shadow: 0 18px 48px rgba(11,19,43,0.075);
        }
        .directory-card-grid {
          gap: 12px !important;
        }
        .directory-masjid-card {
          position: relative;
          overflow: hidden;
          gap: 12px !important;
          padding: 12px !important;
          border-color: rgba(14,124,97,0.12) !important;
          background:
            linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,250,240,0.86)) !important;
          box-shadow: 0 16px 42px rgba(11,19,43,0.07) !important;
        }
        .directory-masjid-card.is-featured {
          grid-column: span 2;
        }
        .directory-card-visual {
          position: relative;
          min-height: 104px;
          overflow: hidden;
          border-radius: 20px;
          background:
            radial-gradient(circle at 65% 35%, rgba(244,215,137,0.26), transparent 26%),
            linear-gradient(135deg, #071225, #0d3b32);
          color: white;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.12);
        }
        .directory-card-visual-1 {
          background:
            radial-gradient(circle at 62% 35%, rgba(244,215,137,0.3), transparent 26%),
            linear-gradient(135deg, #0a2232, #083c31);
        }
        .directory-card-visual-2 {
          background:
            radial-gradient(circle at 62% 35%, rgba(56,189,248,0.19), transparent 28%),
            linear-gradient(135deg, #071225, #263111);
        }
        .directory-card-visual-3 {
          background:
            radial-gradient(circle at 62% 35%, rgba(244,215,137,0.24), transparent 28%),
            linear-gradient(135deg, #10281f, #061527);
        }
        .directory-card-arch,
        .directory-card-visual-glow {
          position: absolute;
          pointer-events: none;
        }
        .directory-card-arch {
          right: 18px;
          bottom: -32px;
          width: 110px;
          height: 110px;
          border-radius: 58px 58px 0 0;
          border: 1px solid rgba(244,215,137,0.34);
          background:
            radial-gradient(circle at 50% 15%, rgba(244,215,137,0.72) 0 4px, transparent 5px),
            linear-gradient(180deg, rgba(244,215,137,0.22), rgba(255,255,255,0.04));
          box-shadow: 0 0 36px rgba(244,215,137,0.16);
        }
        .directory-card-visual-glow {
          inset: auto 20px -34px auto;
          width: 150px;
          height: 90px;
          border-radius: 999px;
          background: rgba(244,215,137,0.18);
          filter: blur(22px);
        }
        .directory-card-visual span,
        .directory-card-visual b {
          position: absolute;
          left: 14px;
          z-index: 2;
        }
        .directory-card-visual span {
          top: 12px;
          padding: 6px 8px;
          border-radius: 999px;
          background: rgba(255,255,255,0.12);
          color: rgba(255,250,240,0.82);
          font-size: 10px;
          font-weight: 900;
          backdrop-filter: blur(12px);
        }
        .directory-card-visual b {
          bottom: 14px;
          font-size: 15px;
        }
        .directory-card-metrics div {
          padding: 9px !important;
          background: rgba(255,255,255,0.78) !important;
        }
        .directory-ai-note {
          background:
            radial-gradient(circle at 0% 0%, rgba(14,124,97,0.12), transparent 30%),
            rgba(236,253,245,0.72) !important;
        }
        .directory-ai-context-card h3 {
          margin: 0;
          color: #061527;
          font-size: 18px;
        }
        .directory-ai-context-list {
          display: grid;
          gap: 8px;
        }
        .directory-ai-context-list span {
          padding: 9px 10px;
          border: 1px solid rgba(14,124,97,0.12);
          border-radius: 14px;
          background: rgba(255,255,255,0.72);
          color: #334155;
          font-size: 12px;
          font-weight: 850;
        }
        .directory-community-card .premium-mobile-stack > div {
          border-top-color: rgba(14,124,97,0.1) !important;
        }
        .directory-add-card {
          background:
            radial-gradient(circle at 18% 10%, rgba(25,201,148,0.26), transparent 30%),
            radial-gradient(circle at 88% 18%, rgba(244,215,137,0.2), transparent 28%),
            linear-gradient(135deg, #061527, #0c2f2a) !important;
        }
        .directory-premium-page .directory-hero {
          padding: 0 !important;
        }
        .directory-premium-page .directory-hero .directory-hero-inner {
          padding-block: 22px 24px !important;
        }
        .directory-premium-page .directory-hero .directory-hero-copy h1 {
          font-size: clamp(2.25rem, 3.4vw, 3.3rem) !important;
        }
        @keyframes directoryPulse {
          0%, 100% { transform: scale(1); opacity: 0.72; }
          50% { transform: scale(1.38); opacity: 1; }
        }
        @keyframes directoryShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* Operational realism finishing pass */
        .directory-premium-page .directory-map-lines path {
          stroke-dasharray: 8 12;
          animation: directoryTrace 11s linear infinite;
        }

        .directory-premium-page .directory-map-node {
          animation: directoryNodeBreathe 3.8s ease-in-out infinite;
        }

        .directory-premium-page .directory-map-node:nth-of-type(2n) {
          animation-delay: 0.7s;
        }

        .directory-premium-page .directory-map-hud,
        .directory-premium-page .directory-map-card,
        .directory-premium-page .directory-nearby-panel {
          backdrop-filter: blur(18px);
          transition: transform 180ms ease, box-shadow 180ms ease;
        }

        .directory-premium-page .directory-map-card:hover,
        .directory-premium-page .directory-masjid-card:hover {
          transform: translateY(-3px);
        }

        .directory-premium-page .directory-locality-strip {
          position: relative;
          overflow: hidden;
        }

        .directory-premium-page .directory-locality-strip::before {
          content: "";
          position: absolute;
          left: 14px;
          top: 50%;
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #19c994;
          box-shadow: 0 0 0 7px rgba(25,201,148,0.09), 0 0 18px rgba(25,201,148,0.34);
          transform: translateY(-50%);
          animation: directoryNodeBreathe 3.3s ease-in-out infinite;
        }

        .directory-premium-page .directory-locality-strip > div:first-child {
          padding-left: 18px;
        }

        .directory-premium-page .directory-masjid-card:nth-child(4n + 1) {
          border-color: rgba(14,124,97,0.2);
          background:
            radial-gradient(circle at 100% 0%, rgba(14,124,97,0.12), transparent 28%),
            linear-gradient(145deg, rgba(255,255,255,0.97), rgba(255,250,240,0.82));
        }

        .directory-premium-page .directory-masjid-card:nth-child(4n + 3) {
          border-color: rgba(212,175,55,0.2);
          background:
            radial-gradient(circle at 0% 0%, rgba(212,175,55,0.12), transparent 30%),
            linear-gradient(145deg, rgba(255,255,255,0.96), rgba(255,250,240,0.8));
        }

        .directory-premium-page .directory-ai-note {
          border-color: rgba(14,124,97,0.16);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.7);
        }

        .directory-premium-page .directory-community-card,
        .directory-premium-page .directory-ai-context-card {
          position: relative;
          overflow: hidden;
        }

        .directory-premium-page .directory-community-card::after,
        .directory-premium-page .directory-ai-context-card::after {
          content: "";
          position: absolute;
          inset: auto 18px 14px 18px;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(14,124,97,0.28), transparent);
        }

        @keyframes directoryTrace {
          0% { stroke-dashoffset: 0; opacity: 0.42; }
          50% { opacity: 0.86; }
          100% { stroke-dashoffset: -96; opacity: 0.42; }
        }

        @keyframes directoryNodeBreathe {
          0%, 100% { transform: scale(1); opacity: 0.82; }
          50% { transform: scale(1.16); opacity: 1; }
        }
        @media (max-width: 1120px) {
          .directory-main-layout {
            grid-template-columns: 1fr;
          }
          .directory-rail {
            position: static;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .directory-card-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 768px) {
          .directory-hero-inner {
            grid-template-columns: 1fr !important;
            padding-block: 22px 18px !important;
            gap: 18px !important;
          }
          .directory-hero-copy,
          .directory-search-panel {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
          }
          .directory-hero-copy h1 {
            font-size: clamp(2rem, 8.6vw, 2.75rem) !important;
            line-height: 1.05 !important;
          }
          .directory-hero-copy p {
            font-size: 14px !important;
            line-height: 1.55 !important;
          }
          .directory-hero-signal-strip {
            flex-wrap: nowrap;
            overflow-x: auto;
            padding-bottom: 3px;
            scrollbar-width: none;
          }
          .directory-hero-signal-strip::-webkit-scrollbar {
            display: none;
          }
          .directory-hero-signal-strip span {
            flex: 0 0 auto;
            font-size: 10px;
          }
          .directory-search-panel {
            padding: 14px !important;
            border-radius: 22px !important;
            gap: 10px !important;
          }
          .directory-ai-search {
            align-items: center !important;
            flex-direction: row !important;
            flex-wrap: wrap !important;
            gap: 8px !important;
            padding: 9px !important;
            min-height: auto !important;
          }
          .directory-ai-search button[aria-label="Voice search ready"] {
            display: none !important;
          }
          .directory-ai-search input,
          .directory-ai-search .ui-button {
            width: auto;
          }
          .directory-ai-search input {
            flex: 1 1 180px !important;
            height: 38px !important;
            font-size: 12px !important;
          }
          .directory-ai-search .premium-button {
            flex: 1 0 100% !important;
            min-height: 40px !important;
            font-size: 12px !important;
          }
          .directory-search-meta {
            align-items: center;
            flex-direction: row;
            gap: 8px !important;
            font-size: 10px !important;
          }
          .directory-search-meta span:nth-of-type(2) {
            display: none;
          }
          .directory-trending {
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            padding-bottom: 3px;
            scrollbar-width: none;
          }
          .directory-trending::-webkit-scrollbar {
            display: none;
          }
          .directory-trending button {
            flex: 0 0 auto;
            padding: 7px 10px !important;
            font-size: 11px !important;
          }
          .directory-sticky-search,
          .directory-floating-map {
            display: flex;
          }
          .directory-sticky-search {
            display: block;
          }
          .directory-map-wrap,
          .directory-rail,
          .directory-card-grid {
            grid-template-columns: 1fr !important;
          }
          .directory-locality-strip {
            grid-template-columns: 1fr;
            gap: 8px;
            padding: 10px;
          }
          .directory-map-surface {
            width: 100% !important;
            max-width: 100% !important;
            min-height: 320px !important;
            border-radius: 22px !important;
          }
          .directory-map-hud {
            top: 12px;
            left: 12px;
            width: calc(100% - 24px);
            padding: 11px;
          }
          .directory-map-card {
            left: 12px;
            bottom: 12px;
            width: calc(100% - 24px);
            padding: 12px;
          }
          .directory-map-legend {
            display: none;
          }
          .directory-nearby-panel {
            padding: 14px;
          }
          .directory-card-metrics {
            grid-template-columns: 1fr;
          }
          .directory-masjid-card.is-featured {
            grid-column: span 1;
          }
          .directory-card-visual {
            min-height: 112px;
          }
          .directory-filter-system {
            max-height: 138px;
            overflow: hidden;
          }
          .directory-card-topline,
          .directory-card-footer {
            align-items: flex-start;
            flex-direction: column;
          }
          .directory-premium-page footer .container > div:first-child {
            grid-template-columns: 1fr !important;
            gap: 28px !important;
          }
        }
      `}</style>
    </div>
  )
}
